import DownloadIcon from '@mui/icons-material/GetApp';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import {
  AppBar,
  Box,
  Button,
  Container,
  Grow,
  Slide,
  Toolbar,
  Tooltip,
} from '@mui/material';
import isEqual from 'lodash/isEqual';
import {fromExtent as polygonFromExtent} from 'ol/geom/Polygon';
import ImageLayer from 'ol/layer/Image';
import ImageCanvas from 'ol/source/ImageCanvas';
import PropTypes from 'prop-types';
import React, {Component, Fragment} from 'react';
import {withTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import PlanetLogoDark from '../../assets/logos/logo-dark.svg';
import PlanetLogoWhite from '../../assets/logos/logo-light.svg';
import AspectRatioMenu from './snapshot-aspect-ratio-menu.jsx';
import DownloadDialog from './snapshot-download-dialog.jsx';
import FooterStyleMenu from './snapshot-footer-style-menu.jsx';
import LoadingBackdrop from './snapshot-loading-backdrop.jsx';
import OverlayOptionsMenu from './snapshot-overlay-options-menu.jsx';
import {DownloadButton, ToggleButton} from './snapshot.styles.jsx';
import {setMode} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';
import {TILE_MODE, UI_MODES, coordinateSystems} from '@explorer/constants';
import {getSelectedFolderResultsDateRange} from '@explorer/selectors/folders';
import {getSelectedMosaic} from '@explorer/selectors/mosaics';
import {getSelectedResultsDateRange} from '@explorer/selectors/results';
import {getSearchGeometryGeoJson} from '@explorer/selectors/search';
import formatLocation from '@explorer/util/format-location';
import formatMosaicDate from '@explorer/util/format-mosaic-date';
import {getExtent} from '@explorer/util/geom';
import {WEEK} from '@explorer/util/intervals';
import {
  ASPECT_RATIOS,
  EXPORT_SIZES,
  FOOTER_STYLES,
  getHeightFromWidth,
} from '@explorer/util/snapshot';

/* Constants & Settings */
export const OVERLAY_PADDING = 130;
export const ANIMATION_DURATION = 300;
const PIXEL_RATIO = window.devicePixelRatio;
export const INFOBAR_HEIGHT = 80 * PIXEL_RATIO;

const DEFAULT_OPTIONS = {
  meta: true, // "show options" option
  bar: FOOTER_STYLES.GRADIENT, // "meta footer"
  logo: true,
  minimap: true,
  coords: true,
  dates: true,
};

/* Utils */
// blank canvas needed by ImageCanvasSource
const getCanvas = (extent, resolution, pixelRatio, size) => {
  const [width, height] = size;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  return canvas;
};

/* Export Tools Component */
class Snapshot extends Component {
  constructor(props) {
    super(props);

    this.state = {
      filename: '',
      generatingDownload: false,
      showDownloadDialog: false,
      size: 'medium',
      aspectRatio: ASPECT_RATIOS.wide,
      options: {...DEFAULT_OPTIONS},
    };
  }

  componentDidMount() {
    // Set the initial overlay dimensions
    this.overlayDimensions = this.updateOverlayDimensions();
    // Set Planet Logo
    this.planetLogoLight = new Image();
    this.planetLogoLight.src = PlanetLogoWhite;
    this.planetLogoDark = new Image();
    this.planetLogoDark.src = PlanetLogoDark;

    // Set Globe
    this.globeCanvas = document
      .getElementsByClassName('pl-globe')[0]
      .getElementsByTagName('canvas')[0];

    // Create the Export Meta Layer
    this.exportMetaLayer = new ImageLayer({
      visible: false,
      name: 'export-meta',
      source: new ImageCanvas({
        canvasFunction: getCanvas,
        ratio: 1,
        projection: 'EPSG:3857',
      }),
    });
    // Set the pre-compose function for export meta layer
    this.exportMetaLayer.on('prerender', this.onMetaPreCompose);

    // Add the Export Meta Layer to the map
    this.props.map.addLayer(this.exportMetaLayer);

    // Create the Export Overlay Layer
    this.exportOverlayLayer = new ImageLayer({
      visible: false,
      name: 'export-overlay',
      source: new ImageCanvas({
        canvasFunction: getCanvas,
        ratio: 1,
        projection: 'EPSG:3857',
      }),
    });
    // Set the pre/post-compose functions for export overlay layer
    this.exportOverlayLayer.on('prerender', this.onPreCompose);
    this.exportOverlayLayer.on('postrender', this.onPostCompose);

    // Add the Export Overlay Layer to the map
    this.props.map.addLayer(this.exportOverlayLayer);

    // Set the initial animation type
    this.animationType = 'fade';

    // Load the overlays if page is loaded directly in snapshot mode
    if (this.props.active) {
      this.exportMetaLayer.setVisible(true);
      this.exportOverlayLayer.setVisible(true);
    }
  }

  componentWillUnmount() {
    this.props.map.removeLayer(this.exportMetaLayer);
    this.props.map.removeLayer(this.exportOverlayLayer);
  }

  componentDidUpdate(prevProps, prevState) {
    // Toggle visibility of the Export Overlay Layer
    if (prevProps.active !== this.props.active) {
      this.animationType = 'fade';
      this.animationStart = new Date().getTime();

      if (this.props.active) {
        this.exportMetaLayer.setVisible(true);
        // Make the export overlay layer visible (causes fade in animation)
        this.exportOverlayLayer.setVisible(true);
      } else {
        this.exportMetaLayer.setVisible(false);
        this.exportOverlayLayer.setVisible(false);
      }
    }

    // If aspect ratio changes
    if (this.state.aspectRatio !== prevState.aspectRatio) {
      this.reRenderOverlay();
    }

    if (this.state.options !== prevState.options) {
      this.props.map.render();
    }

    if (
      !isEqual(prevProps.windowSize, this.props.windowSize) ||
      this.props.viewport !== prevProps.viewport
    ) {
      // Handle browser window resize
      // But not while we are generating the update
      if (!this.state.generatingDownload) {
        this.props.map.updateSize();
        this.overlayDimensions = this.updateOverlayDimensions();
      }
    }
  }

  // Re-render the overlay for resizes
  reRenderOverlay = () => {
    this.animationType = 'resize';
    this.animationStart = new Date().getTime();

    // Record Previous Overlay Dimensions
    this.prevOverlayDimensions = this.overlayDimensions;

    // Update dimensions
    this.overlayDimensions = this.updateOverlayDimensions();

    // And re-render map
    this.props.map.render();
  };

  // Update the overlay dimensions
  updateOverlayDimensions = () => {
    // short circuit if there is no map to calculate
    //   dimeensions against.
    const mapSize = this.props.map.getSize();
    if (!mapSize) {
      return false;
    }
    // Setup the map size
    const [width, height] = mapSize;

    const sidebar = 0;

    // Calculate the Export Overlay Dimensions
    const mapWidth = PIXEL_RATIO * width - sidebar - OVERLAY_PADDING * 2;
    const mapHeight = PIXEL_RATIO * height - OVERLAY_PADDING * 2;

    // Calculate the CANVAS aspect ratio
    const canvasAspectRatio = mapWidth / mapHeight;

    // Setup overlay dimensions variable
    let overlayDimensions = {};

    // Set the overlay dimensions
    if (this.state.aspectRatio < canvasAspectRatio) {
      // Horizontal Window
      overlayDimensions = {
        width: mapHeight * this.state.aspectRatio,
        height: mapHeight,
        top: OVERLAY_PADDING,
        left:
          (PIXEL_RATIO * width) / 2 - (mapHeight * this.state.aspectRatio) / 2,
      };
    } else {
      // Vertical Window
      overlayDimensions = {
        width: mapWidth,
        height: mapWidth * (1 / this.state.aspectRatio),
        top:
          (PIXEL_RATIO * height) / 2 -
          (mapWidth * (1 / this.state.aspectRatio)) / 2,
        left: OVERLAY_PADDING + sidebar,
      };
    }

    return overlayDimensions;
  };

  onPreCompose = event => {
    //Draw the Overlay
    this.drawOverlay(event);
  };

  onPostCompose = event => {
    const ctx = event.context;
    ctx.restore();
  };

  // Precompose hook for Meta Layer
  onMetaPreCompose = event => {
    this.drawMeta(event);
  };

  // Precompose hook for GENERATED Export Canvas/Map Meta Layer
  onMetaPreComposeGen = event => {
    // Draw the meta with the correct size
    this.drawMeta(event, true);
  };

  // Get the "date" for imagery being displayed
  getDate = () => {
    // Mosaics
    if (this.props.tileMode === TILE_MODE.BASEMAPS) {
      if (this.props.selectedMosaic) {
        const dateRange = this.props.t(
          ...formatMosaicDate(this.props.selectedMosaic)
        );
        return this.props.selectedMosaic &&
          this.props.selectedMosaic.interval === WEEK
          ? this.props.t('snapshot.descriptionWeek', {dateRange})
          : this.props.t('snapshot.descriptionOther', {dateRange});
      } else {
        return '';
      }
    } else if (this.props.tileMode === TILE_MODE.FOLDERS) {
      return this.props.selectedFolderResultsDateInterval
        .map(date => this.props.t('common:date.long', {date: date}))
        .join(' - ');
    } else {
      // Scenes
      return this.props.selectedScenesDateInterval
        .map(date => this.props.t('common:date.long', {date: date}))
        .join(' - ');
    }
  };

  drawMeta = (event, gen) => {
    // TODO: Enhancement: break each option type in different rendering functions
    // so we don't recalculate everything on each compose/draw
    const {options} = this.state;
    //Canvas context
    const ctx = event.context;

    // Get the canvas size
    const canvasSize = {
      width: event.context.canvas.width,
      height: event.context.canvas.height,
    };

    // Overlay dimensions
    // 13 Apr 2022 [Duck]: This is a little sloppy but there can be some
    //                      serious state-sync issues with the main map.
    this.overlayDimensions = this.updateOverlayDimensions();
    const overlayDimensions = this.overlayDimensions;

    // Padding
    const padding = 10 * PIXEL_RATIO;

    // Minimap size
    const miniMapSize = 60 * PIXEL_RATIO;

    // Set styles for meta
    ctx.fillStyle = 'rgba(255,255,255,0.9)';

    ctx.globalAlpha = 1;

    /* Draw meta */

    // Infobox styling variable
    const infoBoxGradient = options.bar === FOOTER_STYLES.GRADIENT;

    // If the infobar is a gradient, the box get's bigger
    const infoBarHeightMod = infoBoxGradient ? 2 : 1.5;

    // Info Bar
    if (options.bar !== FOOTER_STYLES.NONE) {
      // Infobar Gradient
      const gradient = ctx.createLinearGradient(
        // x0
        0,
        // y0
        gen
          ? canvasSize.height - INFOBAR_HEIGHT * infoBarHeightMod
          : overlayDimensions.top +
              overlayDimensions.height -
              INFOBAR_HEIGHT * infoBarHeightMod,
        // x1
        0,
        // y1
        gen
          ? canvasSize.height
          : overlayDimensions.top + overlayDimensions.height
      );

      gradient.addColorStop(0, 'rgba(0,31,35,0)');
      gradient.addColorStop(1, 'rgba(0,31,35,0.9)');

      // Infobox background color
      ctx.fillStyle = infoBoxGradient
        ? gradient
        : options.bar === 'dark'
          ? 'rgba(0,31,35, 0.8)'
          : 'rgba(255,255,255,0.7)';

      // Draw Bar Rectangle
      ctx.fillRect(
        // x
        gen ? 0 : overlayDimensions.left + 1,
        // y
        (gen
          ? canvasSize.height
          : overlayDimensions.top + overlayDimensions.height) -
          INFOBAR_HEIGHT * infoBarHeightMod,
        // width
        gen ? canvasSize.width : overlayDimensions.width - 1,
        // height
        INFOBAR_HEIGHT * infoBarHeightMod
      );
    }

    /* Logo */
    if (options.logo) {
      // Draw Logo
      ctx.drawImage(
        // src
        options.bar === 'light' ? this.planetLogoDark : this.planetLogoLight,
        // dX
        (gen ? 0 : overlayDimensions.left) + padding,
        // dY
        (gen
          ? canvasSize.height
          : overlayDimensions.top + overlayDimensions.height) -
          INFOBAR_HEIGHT -
          padding * 2.75, // create spacing to account for disclaimer
        // Width
        123 * PIXEL_RATIO,
        // Height
        60 * PIXEL_RATIO
      );
    }

    /* Meta Data (coordinates, dates) */

    // Text Styles
    ctx.fillStyle = options.bar === 'light' ? 'black' : 'white';
    const defaultFontSize = 14 * PIXEL_RATIO;
    ctx.font = `${defaultFontSize}px Arial`;

    // Text Positioning

    // Offset accounting for minimap
    const metaDataXOffest = options.minimap ? miniMapSize + padding : 0;

    // Offset to account for bottom "bar"
    const metadataYOffset = 16 * PIXEL_RATIO;

    // Meta data (coords and date) X position
    const metaDataX =
      (gen
        ? canvasSize.width
        : overlayDimensions.left + overlayDimensions.width) -
      (padding + metaDataXOffest);

    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    /*  Coordinates */
    const coordsYOffset =
      options.minimap && !options.dates
        ? padding + miniMapSize / 2.2
        : padding * 1.3;

    // Get the coordinates either from main map or generated map
    if (options.coords) {
      // Get Center of view
      const centerCoords = this.props.map.getView().getCenter();

      const centerCoordsText = `${formatLocation(
        centerCoords,
        coordinateSystems.dms
      )}`;

      // Draw Coordinates
      ctx.fillText(
        // Text
        centerCoordsText,
        // X
        metaDataX,
        // Y
        (gen
          ? canvasSize.height
          : overlayDimensions.top + overlayDimensions.height) -
          coordsYOffset -
          metadataYOffset
      );
    }

    const datesFontSize = 16 * PIXEL_RATIO;
    /* Dates */
    if (options.dates) {
      // Set the text style for date
      ctx.fillStyle = options.bar === 'light' ? 'black' : 'white';
      ctx.font = `normal normal bold ${datesFontSize}px Arial`;

      // Calculate the offset for positioning date text
      let datesYOffset;
      if (options.coords) {
        datesYOffset = padding + defaultFontSize * 1.75;
      } else {
        if (options.minimap) {
          datesYOffset = miniMapSize / 2;
        } else {
          datesYOffset = padding * 2;
        }
      }

      const isMosaic = this.props.tileMode === TILE_MODE.BASEMAPS;
      // Add the mosaic series name
      if (isMosaic) {
        // Set the text style for the series name
        ctx.font = `normal normal bold ${datesFontSize} px Arial`;

        // Draw the series name
        ctx.fillText(
          // Text
          this.props.selectedMosaic?.seriesName
            ? `${this.props.selectedMosaic?.seriesName} Basemap`
            : '',
          // X
          metaDataX,
          // Y
          (gen
            ? canvasSize.height
            : overlayDimensions.top + overlayDimensions.height) -
            datesYOffset -
            metadataYOffset -
            (datesFontSize + 4)
        );

        // Set text style for the date
        ctx.font = `normal normal ${14 * PIXEL_RATIO}px Arial`;
        ctx.fillStyle = options.bar !== 'light' ? 'white' : 'black';
      }

      // Draw the date
      ctx.fillText(
        // Text
        this.getDate(),
        // X
        metaDataX,
        // Y
        (gen
          ? canvasSize.height
          : overlayDimensions.top + overlayDimensions.height) -
          datesYOffset -
          metadataYOffset
      );
    }

    /* MiniMap */
    if (options.minimap) {
      ctx.drawImage(
        this.globeCanvas,
        (gen
          ? canvasSize.width
          : overlayDimensions.left + overlayDimensions.width) -
          miniMapSize -
          padding,
        // Y
        (gen
          ? canvasSize.height
          : overlayDimensions.top + overlayDimensions.height) -
          miniMapSize -
          padding / 1.5 -
          metadataYOffset,
        miniMapSize,
        miniMapSize
      );
    }

    /* Disclaimer -- see WEBAPPS-1036 */
    ctx.font = `normal normal ${12 * PIXEL_RATIO}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(
      // Text
      this.props.t('snapshot.watermarkDownload'),
      // X
      (gen ? 0 : overlayDimensions.left) + padding,
      // Y
      (gen
        ? canvasSize.height
        : overlayDimensions.top + overlayDimensions.height) -
        padding -
        metadataYOffset -
        4
    );

    /* Attribution */
    if (this.props.showAttribution) {
      ctx.font = `normal normal ${12 * PIXEL_RATIO}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText(
        // Text
        this.props.t('snapshot.watermarkAttributions'),
        // X
        (gen ? 0 : overlayDimensions.left) + padding,
        // Y
        (gen
          ? canvasSize.height
          : overlayDimensions.top + overlayDimensions.height) - padding
      );
    }
  };

  // Draw the overlay
  drawOverlay = event => {
    // Canvas context
    const ctx = event.context;
    const {width, height} = ctx.canvas;
    // Overlay dimensions
    const overlayDimensions = this.overlayDimensions;
    // Determines fade in vs fade out via 'active' prop
    const fadeModifier = this.props.active;

    // Save the context so we can clear each animation frame but not loose the map
    const savedCtx = ctx.getImageData(0, 0, width, height);

    // Adjustment For Canvas Lines
    const adj = 1 / 3;

    // Draw the overlay
    const tick = () => {
      // Calculate elapsed time
      const timeNow = new Date().getTime();
      const elapsedTime = timeNow - this.animationStart;

      // Overlay Mask color
      ctx.fillStyle = '#161822';

      // The percentage of the animation that has completed
      // Uses fadeModifier to reverse for fade out
      const percentageAnimation =
        elapsedTime / ANIMATION_DURATION < 1
          ? fadeModifier
            ? elapsedTime / ANIMATION_DURATION
            : 1 - elapsedTime / ANIMATION_DURATION
          : fadeModifier
            ? 1
            : 0;

      // Clear each animation frame
      ctx.clearRect(0, 0, width, height);
      // Load saved map
      ctx.putImageData(savedCtx, 0, 0);

      // Overlay alpha
      ctx.globalAlpha =
        0.8 * (this.animationType === 'fade' ? percentageAnimation : 1);

      // Cacluate Dimensions for resized viewport
      const calcResizedViewportDimensions = dimension =>
        this.prevOverlayDimensions &&
        this.prevOverlayDimensions[dimension] -
          (this.prevOverlayDimensions[dimension] -
            overlayDimensions[dimension]) *
            percentageAnimation +
          adj;

      // Overlay background cutout exterior
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, height);
      ctx.lineTo(width, height);
      ctx.lineTo(width, 0);

      // Overlay background cutout interior

      // If the animation is fading in/out, draw from edges
      if (this.animationType === 'fade') {
        ctx.rect(
          percentageAnimation * overlayDimensions.left + adj,
          percentageAnimation * overlayDimensions.top + adj,
          width - (width - overlayDimensions.width - adj) * percentageAnimation,
          height -
            (height - overlayDimensions.height - adj) * percentageAnimation
        );
      } else {
        // Otherwise draw/resize from the old cutout rectangle
        ctx.rect(
          calcResizedViewportDimensions('left'),
          calcResizedViewportDimensions('top'),
          calcResizedViewportDimensions('width'),
          calcResizedViewportDimensions('height')
        );
      }

      ctx.closePath();

      ctx.fill();

      // Grid
      ctx.lineWidth = 1 + adj;
      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.setLineDash([4, 10]);
      ctx.beginPath();

      // TODO: animate the grid
      for (
        let x = overlayDimensions.left + overlayDimensions.width / 3;
        x <= overlayDimensions.width + overlayDimensions.left - 20;
        x += overlayDimensions.width / 3
      ) {
        for (
          let y = overlayDimensions.top + overlayDimensions.height / 3;
          y <= overlayDimensions.height + overlayDimensions.top - 20;
          y += overlayDimensions.height / 3
        ) {
          ctx.moveTo(x + adj, overlayDimensions.top + adj);
          ctx.lineTo(
            x + adj,
            overlayDimensions.height + overlayDimensions.top + adj
          );

          ctx.moveTo(overlayDimensions.left + adj, y + adj);
          ctx.lineTo(
            overlayDimensions.width + overlayDimensions.left + adj,
            y + adj
          );
        }
      }
      ctx.closePath();
      ctx.stroke();

      // Border
      ctx.lineWidth = 1 + adj;
      ctx.strokeStyle = '#e6e6e6';
      ctx.setLineDash([0]);

      ctx.beginPath();

      // If the animation is fading in/out, draw from edges
      if (this.animationType === 'fade') {
        ctx.strokeRect(
          percentageAnimation * overlayDimensions.left + adj,
          percentageAnimation * overlayDimensions.top + adj,
          width - (width - overlayDimensions.width - adj) * percentageAnimation,
          height -
            (height - overlayDimensions.height - adj) * percentageAnimation
        );
      } else {
        // Otherwise draw/resize from the old border rectangle

        // Draw new border rectangle
        ctx.strokeRect(
          calcResizedViewportDimensions('left'),
          calcResizedViewportDimensions('top'),
          calcResizedViewportDimensions('width'),
          calcResizedViewportDimensions('height')
        );
      }

      ctx.closePath();

      /* Tick the animation */

      // If we're within animation duration
      if (elapsedTime < ANIMATION_DURATION) {
        // animate
        requestAnimationFrame(tick);
      }
    };
    tick();
  };

  // Handle Aspect Ratio Selection
  onAspectRatioSelect = ratio => {
    this.setState({aspectRatio: ratio});
  };

  // Handle Setting Size
  onSizeSelect = (event, size) => size && this.setState({size: size});

  // Click on Fit AOI
  onFitAoiClick = () => {
    if (this.props.searchGeo) {
      const extent = getExtent(this.props.searchGeo);

      // Fit the map view (and viewport) to aoi
      this.props.map
        .getView()
        .fit([extent.left, extent.bottom, extent.right, extent.top], {
          size: [
            this.overlayDimensions.width / PIXEL_RATIO,
            this.overlayDimensions.height / PIXEL_RATIO,
          ],
          padding: [20, 20, 20, 20],
          constrainResolution: false,
          duration: 1000,
        });
    }
  };

  // Toggle an Option
  handleToggleOption = option => {
    // Set the approriate option value in state
    const newOptions = {...{}, ...this.state.options};
    newOptions[option] = !this.state.options[option];

    this.setState({options: newOptions});
  };

  // Set the bar style
  setBarStyle = option => {
    this.setState({options: {...this.state.options, bar: option}});
  };

  generateFileName = () => {
    const prefix = 'planet-snapshot';

    // Scenes
    if (this.props.tileMode === TILE_MODE.RESULTS) {
      const date = this.props.selectedScenesDateInterval
        .map(date => {
          return this.props.t('snapshot.exportDate', {
            date: date,
          });
        })
        .join('-');
      return `${prefix}-${date}`;
    } else if (this.props.tileMode === TILE_MODE.FOLDERS) {
      const date = this.props.selectedFolderResultsDateInterval
        .map(date => {
          return this.props.t('snapshot.exportDate', {
            date: date,
          });
        })
        .join('-');
      return `${prefix}-${date}`;
    } else {
      // Mosaics
      const seriesName = this.props.selectedMosaic?.seriesName
        ? this.props.selectedMosaic.seriesName
            .replace(/\s+/g, '-')
            .toLowerCase() + '-'
        : '-';
      const date = this.props.selectedMosaic
        ? this.props
            .t(...formatMosaicDate(this.props.selectedMosaic))
            .replace(/\s+/g, '-')
            .toLowerCase()
        : '';
      return `${prefix}-${seriesName}${date}`;
    }
  };

  // Generate Export
  generateSnapshot = () => {
    /* Caluclate dimensions */
    // Get the polygon (bounds) of the current viewport extent
    const bottomLeft = this.props.map.getCoordinateFromPixel([
      Math.floor(this.overlayDimensions.left / PIXEL_RATIO),
      Math.floor(
        (this.overlayDimensions.top + this.overlayDimensions.height) /
          PIXEL_RATIO
      ),
    ]);

    const topRight = this.props.map.getCoordinateFromPixel([
      Math.floor(
        (this.overlayDimensions.left + this.overlayDimensions.width) /
          PIXEL_RATIO
      ),
      Math.floor(this.overlayDimensions.top / PIXEL_RATIO),
    ]);

    const bounds = polygonFromExtent([
      bottomLeft[0],
      bottomLeft[1],
      topRight[0],
      topRight[1],
    ]);

    // Record original viewport size and extent
    const originalSize = this.props.map.getSize();
    const originalExtent = this.props.map
      .getView()
      .calculateExtent(originalSize);

    // Setup new map dimensions
    const width = EXPORT_SIZES[this.state.size];
    const height = getHeightFromWidth(this.state.size, this.state.aspectRatio);
    const size = [width, height];

    // Set map size
    this.props.map.setSize(size);

    // Lock Map Size
    this.props.map.getTargetElement().style.position = 'fixed';
    this.props.map.getTargetElement().style.width = `${width}px`;
    this.props.map.getTargetElement().style.height = `${height}px`;

    // Fit the map view
    this.props.map.getView().fit(bounds, {
      size: size,
      constrainResolution: false, // TODO: DO we need this?
      //nearest: true,
      callback: async () => {
        /* Generate Screenshot Overlay */
        // If meta option is selected
        if (this.state.options.meta) {
          // Create the Generated Export Meta Layer
          this.genExportMetaLayer = new ImageLayer({
            visible: true,
            source: new ImageCanvas({
              canvasFunction: getCanvas,
              ratio: 1,
              projection: 'EPSG:3857',
            }),
          });

          // Set the pre-compose function for Generated export meta layer
          this.genExportMetaLayer.on('prerender', this.onMetaPreComposeGen);

          // Hide the overlay and meta layer previews
          this.exportMetaLayer.setVisible(false);
          this.exportOverlayLayer.setVisible(false);

          // Add the Generated Export Meta Layer to the new map
          this.props.map.addLayer(this.genExportMetaLayer);

          this.props.map.once('rendercomplete', async () => {
            try {
              // Take the Screenshot

              const mapCanvas = document.createElement('canvas');
              const size = this.props.map.getSize();
              mapCanvas.width = size[0];
              mapCanvas.height = size[1];
              const mapContext = mapCanvas.getContext('2d');
              Array.prototype.forEach.call(
                this.props.map
                  .getViewport()
                  .querySelectorAll('.ol-layer canvas, canvas.ol-layer'),
                function (canvas) {
                  if (canvas.width > 0) {
                    const opacity =
                      canvas.parentNode.style.opacity || canvas.style.opacity;
                    mapContext.globalAlpha =
                      opacity === '' ? 1 : Number(opacity);
                    let matrix;
                    const transform = canvas.style.transform;
                    if (transform) {
                      // Get the transform parameters from the style's transform matrix
                      matrix = transform
                        .match(/^matrix\(([^(]*)\)$/)[1]
                        .split(',')
                        .map(Number);
                    } else {
                      matrix = [
                        parseFloat(canvas.style.width) / canvas.width,
                        0,
                        0,
                        parseFloat(canvas.style.height) / canvas.height,
                        0,
                        0,
                      ];
                    }
                    // Apply the transform to the export map context
                    CanvasRenderingContext2D.prototype.setTransform.apply(
                      mapContext,
                      matrix
                    );
                    const backgroundColor =
                      canvas.parentNode.style.backgroundColor;
                    if (backgroundColor) {
                      mapContext.fillStyle = backgroundColor;
                      mapContext.fillRect(0, 0, canvas.width, canvas.height);
                    }
                    mapContext.drawImage(canvas, 0, 0);
                  }
                }
              );

              mapContext.globalAlpha = 1;
              mapContext.setTransform(1, 0, 0, 1, 0, 0);

              // Construct the filename
              const filename = this.state.filename.replace(/\s+|\/|\\/g, '_');

              // Handle Download
              const element = document.createElement('a');
              element.setAttribute('href', mapCanvas.toDataURL());
              element.setAttribute('download', filename);
              element.style.display = 'none';
              document.body.appendChild(element);
              element.click();
              document.body.removeChild(element);
              trackEvent('Snapshot: Snapshot Generation Succeeded');
            } catch (ex) {
              alert(ex.message);
              trackEvent('Snapshot: Snapshot Generation Failed');
            } finally {
              /* Clean Up */

              // Remove the Generated Export Meta Layer
              this.props.map.removeLayer(this.genExportMetaLayer);

              // Show the overlay and meta layers
              this.exportOverlayLayer.setVisible(true);
              this.exportMetaLayer.setVisible(true);

              // Return view to original size and extent
              this.props.map.getTargetElement().style.position = 'absolute';
              this.props.map.getTargetElement().style.width = '100%';
              this.props.map.getTargetElement().style.height = '100%';

              this.props.map.setSize(originalSize);
              this.props.map
                .getView()
                .fit(originalExtent, {size: originalSize});

              this.setState({generatingDownload: false});
            }
          });
        }
      },
    });
    return;
  };

  // Kick off snapshot download generation
  handleAcceptDownload = () => {
    this.setState({generatingDownload: true});
    this.handleToggleDownloadDialog();
    this.generateSnapshot();

    trackEvent('Snapshot: User Requested Snapshot Generation', {
      tileMode: this.props.tileMode,
      options: this.state.options,
      size: this.state.size,
      aspectRatio: this.state.aspectRatio,
    });
  };

  handleToggleDownloadDialog = () => {
    const showDialog = !this.state.showDownloadDialog;
    if (showDialog) {
      this.setState({filename: this.generateFileName()});
    }
    this.setState({showDownloadDialog: showDialog});
  };

  onChangeFilename = ({target: {value}}) => this.setState({filename: value});

  render() {
    return (
      <Fragment>
        {!this.state.generatingDownload && (
          <Fragment>
            <Slide
              in={this.props.active}
              timeout={ANIMATION_DURATION}
              unmountOnExit
              mountOnEnter
            >
              <AppBar
                sx={theme => ({
                  ...theme.applyStyles('dark', {
                    backgroundColor: 'transparent',
                  }),
                })}
                elevation={0}
                position="sticky"
                className="pl-mode-dark"
              >
                <Toolbar variant="dense">
                  <Container>
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FooterStyleMenu
                          value={this.state.options.bar}
                          onChange={value => this.setBarStyle(value)}
                        />
                        <OverlayOptionsMenu
                          options={this.state.options}
                          onChange={name => {
                            this.handleToggleOption(name);
                          }}
                        />
                        <AspectRatioMenu
                          value={this.state.aspectRatio}
                          onChange={this.onAspectRatioSelect}
                        />
                        <Grow
                          in={
                            !!this.props.searchGeo &&
                            this.props.tileMode !== TILE_MODE.FOLDERS
                          }
                          appear
                        >
                          <ToggleButton
                            tooltipTitle={this.props.t(
                              'snapshot.tooltipSetAoiToSnapshotArea'
                            )}
                            value="fitAOIButton"
                            data-qe="snapshot-fit-aoi-button"
                            onClick={this.onFitAoiClick}
                          >
                            <ZoomOutMapIcon />
                          </ToggleButton>
                        </Grow>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Tooltip
                          title={this.props.t(
                            'snapshot.dowloadButtonTooltip',
                            'Download Snapshot'
                          )}
                          placement="bottom"
                        >
                          <span>
                            <DownloadButton
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<DownloadIcon />}
                              disabled={
                                this.props.tileMode === TILE_MODE.RESULTS &&
                                !this.props.selectedScenesDateInterval.length
                              }
                              onClick={this.handleToggleDownloadDialog}
                            >
                              {this.props.t(
                                'snapshot.downloadButton',
                                'Download'
                              )}
                            </DownloadButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={this.props.t(
                            'snapshot.exitTooltip',
                            'Exit Snapshot Mode'
                          )}
                          placement="bottom"
                        >
                          <Button
                            onClick={() => {
                              this.props.setMode('');
                              trackEvent('Snapshot: Exit Button Clicked');
                            }}
                          >
                            {this.props.t('snapshot.buttonClose')}
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Container>
                </Toolbar>
              </AppBar>
            </Slide>
          </Fragment>
        )}
        <DownloadDialog
          handleToggle={this.handleToggleDownloadDialog}
          open={this.state.showDownloadDialog}
          size={this.state.size}
          selectSize={this.onSizeSelect}
          aspectRatio={this.state.aspectRatio}
          filename={this.state.filename}
          setFilename={this.onChangeFilename}
          handleDownload={this.handleAcceptDownload}
        />
        <LoadingBackdrop open={this.state.generatingDownload} />
      </Fragment>
    );
  }
}

Snapshot.propTypes = {
  active: PropTypes.bool.isRequired,
  t: PropTypes.func,
  map: PropTypes.object.isRequired,
  viewport: PropTypes.object,
  selectedMosaic: PropTypes.object,
  selectedScenesDateInterval: PropTypes.array,
  searchGeo: PropTypes.object,
  setMode: PropTypes.func.isRequired,
  windowSize: PropTypes.object,
  tileMode: PropTypes.string,
  selectedFolderResultsDateInterval: PropTypes.array,
  showAttribution: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => ({
  active: state.ui.mode === UI_MODES.SNAPSHOT,
  map: ownProps.map,
  selectedMosaic: getSelectedMosaic(state),
  selectedScenesDateInterval: getSelectedResultsDateRange(state),
  searchGeo: getSearchGeometryGeoJson(state),
  windowSize: state.ui.size,
  tileMode: state.tiles.mode,
  selectedFolderResultsDateInterval: getSelectedFolderResultsDateRange(state),
});

const mapDispatchToProps = {
  setMode,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Snapshot));
